/* eslint-disable react/prop-types */
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { HomeIcon } from "../icons/SVG";
import useAuth from "../../hooks/useAuth";
import { NavUser } from "./nav-user";
import NavNotification from "./nav-notification";
import OnlineOrderNotification from "./online-order-notification";
import { logo } from "@/assets";
import { AiOutlineFullscreen, AiOutlineFullscreenExit } from "react-icons/ai";
import { CiCalculator2 } from "react-icons/ci";
import { TfiReload } from "react-icons/tfi";
import Calculator from "../lib/Calculator";
import { useEffect, useState } from "react";
import { getUserSettingsApi } from "@/api/settings/take-order-settings";
import { Encrypt } from "@/lib/crypto";
import extractAllKeys from "@/lib/extractAllKeys";
import hasPermission from "@/hooks/hasPermission";
import StaffActionPinModal from "../dialog/StaffActionPinModal";

function Nav2({ LeftBar }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const restaurant_id = user?.restaurant_id;
  const restaurant_system_id = useAuth()?.user?.restaurant_system_id;

  // Staff Break / Clock-Out PIN Modal
  const [staffModalOpen, setStaffModalOpen] = useState(false);
  const [staffActionType, setStaffActionType] = useState("break_in");
  const [isOnBreak, setIsOnBreak] = useState(() => {
    const userId = user?.system_id || user?.id;
    return userId ? localStorage.getItem(`is_on_break_${userId}`) === "true" : false;
  });

  useEffect(() => {
    const userId = user?.system_id || user?.id;
    if (userId) {
      setIsOnBreak(localStorage.getItem(`is_on_break_${userId}`) === "true");
    }
  }, [user]);

  const handleBreakClick = () => {
    setStaffActionType(isOnBreak ? "break_out" : "break_in");
    setStaffModalOpen(true);
  };

  const handleClockOutClick = () => {
    setStaffActionType("clock_out");
    setStaffModalOpen(true);
  };

  const handleStaffActionSuccess = (res, actionType) => {
    const userId = user?.system_id || user?.id;
    if (actionType === "break_in") {
      setIsOnBreak(true);
      if (userId) localStorage.setItem(`is_on_break_${userId}`, "true");
    } else if (actionType === "break_out") {
      setIsOnBreak(false);
      if (userId) localStorage.setItem(`is_on_break_${userId}`, "false");
    }
  };

  // Fullscreen state & handlers 
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Reload confirmation modal
  const [showReloadConfirm, setShowReloadConfirm] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);

  useEffect(() => {
    const onChange = () => {
      const fs =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;
      setIsFullscreen(!!fs);
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    document.addEventListener("mozfullscreenchange", onChange);
    document.addEventListener("MSFullscreenChange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
      document.removeEventListener("mozfullscreenchange", onChange);
      document.removeEventListener("MSFullscreenChange", onChange);
    };
  }, []);

  function toggleFullScreen() {
    const docEl = document.documentElement;
    if (
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    ) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    } else {
      if (docEl.requestFullscreen) {
        docEl.requestFullscreen();
      } else if (docEl.webkitRequestFullscreen) {
        docEl.webkitRequestFullscreen();
      } else if (docEl.mozRequestFullScreen) {
        docEl.mozRequestFullScreen();
      } else if (docEl.msRequestFullscreen) {
        docEl.msRequestFullscreen();
      }
    }
  }

  useEffect(() => {
    if (!showReloadConfirm && !showCalculator) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setShowReloadConfirm(false);
      setShowCalculator(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showReloadConfirm, showCalculator]);

  const handleRefreshAndReload = async () => {
    setShowReloadConfirm(false);
    try {
      const response = await getUserSettingsApi();
      if (response?.status === "success" && response?.data) {
        localStorage.setItem("settings", JSON.stringify(response?.data?.settings ?? {}));
        if (response?.data?.permissions) {
          localStorage.setItem(
            "tags",
            Encrypt(extractAllKeys(response?.data?.permissions)),
          );
        }
        toast.success("Settings updated");
      } else if (response?.status === "failure") {
        toast.error(response?.message || "Failed to fetch settings");
      }
      window.location.reload();
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Failed to fetch settings");
      window.location.reload();
    }
  };

  // Zoom state & handlers 🔧
  // const [zoom, setZoom] = useState(100);

  // useEffect(() => {
  //   // document.body.style.transform = `scale(${zoom / 100})`;
  //   // document.body.style.transformOrigin = "top left";
  //   // if(zoom < 100) {
  //   //   document.body.style.backgroundColor = "#333334";
  //   // } else {
  //   //   document.body.style.backgroundColor = "";
  //   // }
  //   const root = document.getElementById("root");
  //   if (root) {
  //     root.style.zoom = `${zoom}%`;
  //     root.style.transformOrigin = "top left";
  //     root.style.backgroundColor = zoom < 100 ? "#333334" : "";

  //   }

  // }, [zoom]);

  // cleanup on unmount
  // useEffect(() => {
  //   return () => {
  //     document.body.style.transform = "";
  //     document.body.style.transformOrigin = "";
  //     const root = document.getElementById("root");
  //     if (root) {
  //       root.style.zoom = "100%";
  //       root.style.transformOrigin = "";
  //       root.style.backgroundColor = "";
  //     }
  //   };
  // }, []);

  return (
    <div className=" bg-color1 h-14 flex justify-between items-center px-4 border border-x-0 border-t-0 border-b select-none">
      <div className="flex justify-between items-center py-3">
        <div className="flex items-center gap-5">
          <Link
            to={"/home"}
            className={`${
              LeftBar == undefined ? "flex" : "hidden"
            } items-center mr-16`}
          >
            <img className=" w-[150px]" src={logo} alt="logo" />
          </Link>
          <Link
            to="/home"
            className="py-1.5 px-3 gap-2 flex justify-center items-center cursor-pointer bg-cblack7/60 rounded"
          >
            <HomeIcon />
            <span className=" font-semibold text-cwhite">Home</span>
          </Link>
          <div
            onClick={() => { if(hasPermission("take_orders.take_order")) navigate("/take-order") }}
            className="py-1.5 px-3 gap-2 flex justify-center items-center cursor-pointer bg-cyellow rounded"
          >
            <span className=" font-semibold text-cblack flex-shrink-0">
              Take Order
            </span>
          </div>
          <div
            onClick={handleBreakClick}
            className={`py-1.5 px-3 gap-2 flex justify-center items-center cursor-pointer rounded transition-all select-none ${
              isOnBreak
                ? "bg-amber-500/20 text-amber-300 border border-amber-500/40 hover:bg-amber-500/30 shadow-[0_0_10px_rgba(245,158,11,0.2)]"
                : "bg-cblack7/60 hover:bg-cblack7 text-cwhite"
            }`}
          >
            <span className=" font-semibold flex-shrink-0">
              {isOnBreak ? "Break Out" : "Break In"}
            </span>
          </div>
          <div
            onClick={handleClockOutClick}
            className="py-1.5 px-3 gap-2 flex justify-center items-center cursor-pointer bg-cblack7/60 hover:bg-rose-950/40 hover:border-rose-800/40 text-cwhite rounded transition-all select-none"
          >
            <span className=" font-semibold flex-shrink-0">
              Clock-Out
            </span>
          </div>
          {/* <div className="py-1.5 px-3 gap-2 flex justify-center items-center cursor-pointer bg-cblack7/60 rounded">
            <span className=" font-semibold text-cwhite flex-shrink-0">
              Off mode
            </span>
          </div> */}
        </div>
      </div>


        {/* full application zoom in and out */}
        {/* <div className="flex items-center gap-2">
          <span className=" text-color4 mr-2 select-none">Zoom:</span>
          <button
          title="Zoom out"
            onClick={() => setZoom((p) => Math.max(50, p - 1))}
            aria-label="Zoom out"
            className="px-3 py-2 border rounded bg-transparent text-color4"
          >
            <FaMinus />
          </button>
          <span className="min-w-[48px] text-center text-color4">{zoom}%</span>
          <button
            title="Zoom in"
            onClick={() => setZoom((p) => Math.min(150, p + 1))}
            aria-label="Zoom in"
            className="px-3 py-2 border rounded bg-transparent text-color4"
          >
            <FaPlus /> 
          </button>
          <button
            onClick={() => setZoom(100)}
            aria-label="Reset zoom"
            title="Reset zoom"
            className={`ml-2 px-2 py-1 border rounded bg-transparent ${zoom === 100 ? " border-color4 text-color4 " : " border-color6 text-color6"}  `}
          >
            Reset
          </button>
        </div> */}

      <div className="flex items-center gap-5">
        <button
          onClick={() => setShowCalculator(true)}
          title="Calculator"
          aria-label="Calculator"
          className=" border-color4 border p-[4px] rounded-md cursor-pointer text-color4"
        >
          <CiCalculator2 size={24} />
        </button>

        {showCalculator && (
          <Calculator onClose={() => setShowCalculator(false)} />
        )}

        <button
          onClick={() => setShowReloadConfirm(true)}
          title="Refresh application"
          aria-label="Refresh application"
          className=" border-color4 border p-[8px] rounded-md cursor-pointer text-color4"
        >
          <TfiReload  />
        </button>

        {showReloadConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowReloadConfirm(false)} />
            <div
              role="dialog"
              aria-modal="true"
              className="relative bg-white dark:bg-[#1f2937] rounded-lg shadow-lg max-w-md w-full mx-4 p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Refresh application?</h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                Do you want to refresh the application now? Any unsaved changes may be lost.
              </p>
              <div className="mt-4 flex justify-end gap-3">
                <button
                  onClick={() => setShowReloadConfirm(false)}
                  className="px-3 py-2 rounded border border-color5 bg-transparent text-color5"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRefreshAndReload}
                  className="px-3 py-2 rounded bg-cyellow text-cblack font-semibold"
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>
        )}
        <button
        title={isFullscreen ? "Exit full screen" : "Enter full screen"}
          onClick={toggleFullScreen}
          aria-label={isFullscreen ? "Exit full screen" : "Enter full screen"}
          className={` ${isFullscreen ? "border-color6" : "border-color4"} border p-[2px] rounded-md cursor-pointer`}
        >
          {isFullscreen ? (
            <AiOutlineFullscreenExit size={26} className=" text-color6" />
          ) : (
            <AiOutlineFullscreen size={26} className=" text-color4" />
          )}
        </button>
        {
          hasPermission("take_orders.take_order.online_request") &&
          <OnlineOrderNotification />
        }
        
        {
          hasPermission("take_orders.take_order.notification") &&
          <NavNotification setShowReloadConfirm={setShowReloadConfirm} />
        }
        
        <NavUser user={user} />
      </div>

      <StaffActionPinModal
        isOpen={staffModalOpen}
        onClose={() => setStaffModalOpen(false)}
        actionType={staffActionType}
        user={user}
        restaurantId={restaurant_system_id || restaurant_id}
        onSuccess={handleStaffActionSuccess}
      />
    </div>
  );
}

export default Nav2;
